import React, {useState, useEffect} from "react";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {useAuth} from "../AuthContext";
import logo from "../assets/logo.svg";
import buymeacoffeeIcon from "../assets/buymeacoffee-icon.svg";
import discordIcon from "../assets/discord-icon.svg";
import DiscordWidget from "./DiscordWidget";

const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {user, isLoggedIn, signOut, roles, loading} = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDiscordOpen, setIsDiscordOpen] = useState(false);

  // Auto-close mobile menu when screen is resized to desktop size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1065 && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isMobileMenuOpen]);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const isDashboardActive = () => {
    const dashboardPaths = [
      "/overview",
      "/characters",
      "/sessions",
      "/titans",
      "/calendar",
      "/titanmanagement",
      "/sessionmanagement",
      "/usermanagement",
      "/dm-approvals",
      "/feedback-management",
      "/system-logs",
      "/admin-config",
      "/dm-signup",
      "/settings",
    ];
    return (
      location.pathname.startsWith("/env") ||
      dashboardPaths.some((p) => location.pathname.startsWith(p))
    );
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate("/auth/login");
      setIsMobileMenuOpen(false);
    } catch (error) {
      // Logout error - silent failure
    }
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setIsMobileMenuOpen(false);
  };

  const handleMobileNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  const handleDiscordClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDiscordOpen(true);
  };

  // Show loading state
  if (loading) {
    return (
      <header>
        <nav className="navbar">
          <div className="nav-container">
            <div className="nav-logo">
              <Link to="/">
                <img src={logo} alt="League of Fabled Pursuits Logo" />
              </Link>
            </div>
            <ul className="nav-menu">
              <div className="nav-center-group">
                <li className="nav-item">
                  <Link
                    to="/rules"
                    className={`nav-link ${isActive("/rules") ? "active" : ""}`}
                  >
                    Rules
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    to="/feedback"
                    className={`nav-link ${isActive("/feedback") ? "active" : ""}`}
                  >
                    Feedback
                  </Link>
                </li>
              </div>
              <div className="nav-auth-group">
                <li className="nav-item">
                  <span className="nav-link">Loading...</span>
                </li>
                <li className="nav-item">
                  <button
                    onClick={handleDiscordClick}
                    className="btn btn-tertiary nav-icon-button"
                    title="Join our Discord"
                  >
                    <img src={discordIcon} alt="Discord" />
                  </button>
                </li>
                <li className="nav-item">
                  <a
                    href="https://buymeacoffee.com/fabledpursuits"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-tertiary nav-icon-button"
                    title="Support us"
                  >
                    <img src={buymeacoffeeIcon} alt="Buy Me a Coffee" />
                  </a>
                </li>
              </div>
            </ul>
          </div>
        </nav>
      </header>
    );
  }

  return (
    <header>
      <nav className="navbar">
        <div className="nav-container">
          <div className="nav-logo">
            <Link to="/">
              <img src={logo} alt="League of Fabled Pursuits Logo" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <ul className="nav-menu desktop-nav">
            <div className="nav-center-group">
              <li className="nav-item">
                <Link
                  to="/rules"
                  className={`nav-link ${isActive("/rules") ? "active" : ""}`}
                >
                  Rules
                </Link>
              </li>
              {isLoggedIn && (
                <li className="nav-item">
                  <Link
                    to="/overview"
                    className={`nav-link ${isDashboardActive() ? "active" : ""}`}
                  >
                    Dashboard
                  </Link>
                </li>
              )}
              <li className="nav-item">
                <Link
                  to="/feedback"
                  className={`nav-link ${isActive("/feedback") ? "active" : ""}`}
                >
                  Feedback
                </Link>
              </li>
            </div>
            <div className="nav-auth-group">
              {isLoggedIn ? (
                <>
                  <li className="nav-item">
                    <span className="nav-link role-display">
                      {user?.displayName || user?.email || "User"}
                      {roles.length > 0 && ` (${roles.join(", ")})`}
                    </span>
                  </li>
                  <li className="nav-item">
                    <button
                      onClick={handleLogout}
                      className="nav-link nav-login logout-button"
                    >
                      Log out
                    </button>
                  </li>
                </>
              ) : (
                <>
                  <li className="nav-item">
                    <Link
                      to="/auth/register"
                      className={`btn btn-primary ${isActive("/auth/register") ? "active" : ""}`}
                    >
                      Join The League
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link
                      to="/auth/login"
                      className={`btn btn-secondary ${isActive("/auth/login") ? "active" : ""}`}
                    >
                      Log in
                    </Link>
                  </li>
                </>
              )}
              <li className="nav-item">
                <button
                  onClick={handleDiscordClick}
                  className="btn btn-tertiary nav-icon-button"
                  title="Join our Discord"
                >
                  <img src={discordIcon} alt="Discord" />
                </button>
              </li>
              <li className="nav-item">
                <a
                  href="https://buymeacoffee.com/fabledpursuits"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-tertiary nav-icon-button"
                  title="Support us"
                >
                  <img src={buymeacoffeeIcon} alt="Buy Me a Coffee" />
                </a>
              </li>
            </div>
          </ul>

          {/* Mobile Menu Button */}
          <button
            className={`menu-button ${isMobileMenuOpen ? "active" : ""}`}
            onClick={handleMobileMenuToggle}
            aria-label="Toggle mobile menu"
          >
            Menu
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="env-blanket-overlay">
          <div className="mobile-menu active">
            <div className="mobile-menu-header">
              <div className="mobile-menu-logo">
                <img src={logo} alt="League of Fabled Pursuits Logo" />
              </div>
              <button
                className="mobile-menu-close"
                onClick={handleMobileMenuClose}
                aria-label="Close mobile menu"
              >
                MENU
              </button>
            </div>

            <nav className="mobile-nav">
              <ul className="mobile-nav-list">
                <li className="mobile-nav-item">
                  <Link
                    to="/"
                    className={`mobile-nav-link ${isActive("/") ? "active" : ""}`}
                    onClick={handleMobileNavClick}
                  >
                    Home
                  </Link>
                </li>
                <li className="mobile-nav-item">
                  <Link
                    to="/rules"
                    className={`mobile-nav-link ${isActive("/rules") ? "active" : ""}`}
                    onClick={handleMobileNavClick}
                  >
                    Rules
                  </Link>
                </li>
                {isLoggedIn && (
                  <li className="mobile-nav-item">
                    <Link
                      to="/overview"
                      className={`mobile-nav-link ${isDashboardActive() ? "active" : ""}`}
                      onClick={handleMobileNavClick}
                    >
                      Dashboard
                    </Link>
                  </li>
                )}
                <li className="mobile-nav-item">
                  <Link
                    to="/feedback"
                    className={`mobile-nav-link ${isActive("/feedback") ? "active" : ""}`}
                    onClick={handleMobileNavClick}
                  >
                    Feedback
                  </Link>
                </li>
              </ul>

              <div className="mobile-auth-section">
                {isLoggedIn ? (
                  <>
                    <div className="mobile-user-info">
                      <span className="mobile-role-display">
                        {user?.displayName || user?.email || "User"}
                        {roles.length > 0 && ` (${roles.join(", ")})`}
                      </span>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="mobile-logout-btn"
                    >
                      Log out
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/auth/register"
                      className={`btn btn-primary ${isActive("/auth/register") ? "active" : ""}`}
                      onClick={handleMobileNavClick}
                    >
                      Join The League
                    </Link>
                    <Link
                      to="/auth/login"
                      className={`btn btn-secondary ${isActive("/auth/login") ? "active" : ""}`}
                      onClick={handleMobileNavClick}
                    >
                      Log in
                    </Link>
                  </>
                )}
                <div className="mobile-icon-buttons">
                  <button
                    onClick={(e) => {
                      handleDiscordClick(e);
                      handleMobileNavClick();
                    }}
                    className="btn btn-tertiary nav-icon-button"
                    title="Join our Discord"
                  >
                    <img src={discordIcon} alt="Discord" />
                  </button>
                  <a
                    href="https://buymeacoffee.com/fabledpursuits"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-tertiary nav-icon-button"
                    title="Support us"
                    onClick={handleMobileNavClick}
                  >
                    <img src={buymeacoffeeIcon} alt="Buy Me a Coffee" />
                  </a>
                </div>
              </div>
            </nav>
          </div>
        </div>
      )}

      <DiscordWidget
        isOpen={isDiscordOpen}
        onClose={() => setIsDiscordOpen(false)}
      />
    </header>
  );
};

export default Header;
